import { useMemo } from "react";
import { Stack, Typography, Link } from "@mui/material";
import { MarketoHeaderBlock } from "./MarketoHeaderBlock";
import { MarketoLoginData } from "../../types/apiTypes";
import { MarketoLabel } from "./MarketoLabel";
import { ReuseButton, ReuseInput } from "../ui";
import { ButtonsColorType, ButtonsType } from "../../types/enums";

type Field = keyof Omit<MarketoLoginData, "token">;

const requiredFields: Field[] = ["munchkin_id", "client_id", "client_secret"];

const errorMessages: Record<Field, string> = {
  munchkin_id: "Munchkin ID is required",
  client_id: "Client ID is required",
  client_secret: "Client Secret is required",
};

interface MarketoFormSectionProps {
  formData: MarketoLoginData;
  errors: Record<Field, boolean>;
  isLoading: boolean;
  isCreatingMarketoIntegration: boolean;
  onInputChange: (field: string, value: string) => void;
  onSubmit: () => void;
}

export const MarketoFormSection = ({
  formData,
  errors,
  isLoading,
  isCreatingMarketoIntegration,
  onInputChange,
  onSubmit,
}: MarketoFormSectionProps) => {
  const isDisabled = useMemo(
    () =>
      Object.values(errors).some(Boolean) ||
      requiredFields.some((key) => !formData[key].trim()),
    [errors, formData],
  );

  return (
    <Stack gap="32px" maxWidth="496px" alignSelf="center" justifySelf="center">
      <MarketoHeaderBlock />
      <Stack gap="48px" paddingBottom="24px">
        <Stack gap="24px">
          <MarketoLabel />
          <ReuseInput
            name="munchkin_id"
            handleInputChange={onInputChange}
            errorMessage={errors.munchkin_id ? errorMessages.munchkin_id : ""}
            value={formData.munchkin_id}
            label="Munchkin ID"
            placeholder="Munchkin iD (e.g. 123-ABC-456)"
            isError={errors.munchkin_id}
            isDisabled={isLoading}
          />
        </Stack>
        <ReuseInput
          name="client_id"
          handleInputChange={onInputChange}
          errorMessage={errors.client_id ? errorMessages.client_id : ""}
          value={formData.client_id}
          label="Client ID"
          placeholder="Client ID (e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890)"
          isError={errors.client_id}
          isDisabled={isLoading}
        />
        <ReuseInput
          name="client_secret"
          handleInputChange={onInputChange}
          errorMessage={errors.client_secret ? errorMessages.client_secret : ""}
          value={formData.client_secret}
          label="Client Secret"
          placeholder="Client Secret (e.g. AbCdEfGhIjKlMnOpQrStUvWx1234)"
          isError={errors.client_secret}
          isDisabled={isLoading}
        />
      </Stack>
      <ReuseButton
        buttonColor={ButtonsColorType.gradient}
        buttonType={ButtonsType.contained}
        onClick={onSubmit}
        disabled={isDisabled}
        loading={isCreatingMarketoIntegration}
      >
        Continue
      </ReuseButton>
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "22px",
          letterSpacing: "0.3px",
          color: "#797979",
          mt: "16px",
          textAlign: "center",
        }}
      >
        By continuing, you agree to INFUSE's{" "}
        <Link
          href="https://infuse.com/terms-and-conditions/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#2194D2", textDecorationColor: "#2194D2" }}
        >
          Terms of Use
        </Link>{" "}
        and{" "}
        <Link
          href="https://infuse.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#2194D2", textDecorationColor: "#2194D2" }}
        >
          Privacy Policy
        </Link>
      </Typography>
    </Stack>
  );
};
